import { RootAction, RootState } from '@src/modules';
import { clearForgeAutoRedirect, getForgeAutoRedirect } from '@src/services/cookies-service';
import { ForgePath, getForgeOptInUrl } from '@src/services/forge-service';
import { equals } from 'ramda';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  TypedUseSelectorHook,
  useDispatch as ReactReduxUseDispatch,
  useSelector as ReactReduxUseSelector,
} from 'react-redux';
import { Dispatch } from 'redux';

import { SitesSelectors } from '@src/modules/sites';

export const useSelector: TypedUseSelectorHook<RootState> = ReactReduxUseSelector;
export const useDispatch = () => ReactReduxUseDispatch<Dispatch<RootAction>>();

const useDeepCompareMemoize = (value: ([] | object)[]) => {
  const ref = React.useRef<typeof value>();

  if (!equals(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
};

// Use for deep equality check with useEffect
export const useDeepCompareEffect = (callback: () => void, deps: ([] | object)[]) => {
  React.useEffect(callback, useDeepCompareMemoize(deps));
};

export const useSiteId = () => {
  return useSelector(SitesSelectors.getDraftSiteIdOrMasterSiteId);
};

export const useMasterSiteId = () => {
  return useSelector(SitesSelectors.getSelectedSiteIdOrEmpty);
};

export function useForgeNewExperienceParam() {
  const location = useLocation();
  const history = useHistory();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new_experience') === '0') {
      clearForgeAutoRedirect();
      params.delete('new_experience');
      const newSearch = params.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  }, []);
}

export function useForgeAutoRedirect(enabled: boolean, path?: ForgePath) {
  const siteId = useSiteId();

  React.useEffect(() => {
    if (!enabled || !siteId || !getForgeAutoRedirect()) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('new_experience') === '0') return;

    window.location.href = getForgeOptInUrl(siteId, path);
  }, [siteId, enabled, path]);
}
